import { ReactElement, useRef, useState } from 'react';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';

import { useGetLocations } from '../../../services/hooks/useGetLocations';
import { SettingOverride, ValueType } from '../../../types/UserSettings';
import ValueInput from './ValueInput';

const SELECTOR_FIELDS = ['username', 'locationName', 'userCustomerName', 'deviceCustomerId'] as const;
type SelectorField = (typeof SELECTOR_FIELDS)[number];
const SELECTOR_LABELS: Record<SelectorField, string> = {
    username: 'username',
    locationName: 'location_name',
    userCustomerName: 'user_customer_name',
    deviceCustomerId: 'device_customer_id'
};

interface SortableOverrideRowProps {
    id: string;
    override: SettingOverride;
    isExpanded: boolean;
    defaultValue: unknown;
    valueType?: ValueType;
    locationNames: string[];
    onToggle: () => void;
    onChange: (updated: SettingOverride) => void;
    onDelete: () => void;
}

function SortableOverrideRow({
    id,
    override,
    isExpanded,
    defaultValue,
    valueType,
    locationNames,
    onToggle,
    onChange,
    onDelete
}: Readonly<SortableOverrideRowProps>): ReactElement {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const [chipInput, setChipInput] = useState<Record<SelectorField, string>>({
        username: '',
        locationName: '',
        userCustomerName: '',
        deviceCustomerId: ''
    });

    const activeFields = SELECTOR_FIELDS.filter((f) => override.selector[f] != null);
    const availableFields = SELECTOR_FIELDS.filter((f) => override.selector[f] == null);

    const addSelectorField = (field: SelectorField) => {
        onChange({ ...override, selector: { ...override.selector, [field]: [] } });
    };

    const normaliseFieldValues = (field: SelectorField): unknown[] => {
        const raw = override.selector[field];
        if (Array.isArray(raw)) return raw as unknown[];
        if (raw != null) return [raw];
        return [];
    };

    const addChip = (field: SelectorField, raw: string) => {
        const trimmed = raw.trim();
        if (!trimmed) return;
        const isInt = field === 'deviceCustomerId';
        const newVal = isInt ? Number(trimmed) : trimmed;
        if (isInt && !Number.isInteger(newVal)) return;
        const existing = normaliseFieldValues(field);
        onChange({ ...override, selector: { ...override.selector, [field]: [...existing, newVal] } });
        setChipInput((prev) => ({ ...prev, [field]: '' }));
    };

    const removeChip = (field: SelectorField, idx: number) => {
        const existing = normaliseFieldValues(field);
        const updated = existing.filter((_, i) => i !== idx);
        const newSelector = { ...override.selector };
        if (updated.length === 0) {
            delete newSelector[field];
        } else {
            newSelector[field] = updated as string[] & number[];
        }
        onChange({ ...override, selector: newSelector });
    };

    const selectorSummary = activeFields
        .filter((f) => override.selector[f] !== null)
        .map((f) => {
            const vals = override.selector[f];
            return `${SELECTOR_LABELS[f]}: ${Array.isArray(vals) ? vals.join(', ') : vals}`;
        })
        .join(' · ');

    const selectedLocationNames = (override.selector.locationName as string[]) ?? [];

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{ border: 1, borderColor: isExpanded ? 'primary.main' : 'grey.300', borderRadius: 1, mb: 1 }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 1 }}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{ cursor: 'grab', color: 'grey.500', display: isExpanded ? 'none' : 'flex' }}
                >
                    <DragHandleIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {!isExpanded && (
                        <>
                            <Typography variant="body2" noWrap>
                                {override.description || '(no description)'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {selectorSummary} → {JSON.stringify(override.value)}
                            </Typography>
                        </>
                    )}
                    {isExpanded && (
                        <Typography variant="body2" color="primary">
                            Editing override
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={onToggle}>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={onDelete} color="error" aria-label="Delete override">
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            <Collapse in={isExpanded}>
                <Divider />
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                        label="Description (optional)"
                        value={override.description ?? ''}
                        onChange={(e) => onChange({ ...override, description: e.target.value })}
                        size="small"
                        fullWidth
                    />

                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            Selectors — all specified must match
                        </Typography>
                        {activeFields.map((field) => (
                            <Box key={field} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 1, mb: 1 }}>
                                <Typography variant="caption" fontWeight={600}>
                                    {SELECTOR_LABELS[field]}
                                </Typography>
                                <Box
                                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.5 }}
                                >
                                    {normaliseFieldValues(field).map((v, i) => (
                                        <Chip
                                            key={String(v)}
                                            label={String(v)}
                                            size="small"
                                            onDelete={() => removeChip(field, i)}
                                        />
                                    ))}
                                    {field !== 'locationName' && (
                                        <input
                                            value={chipInput[field]}
                                            onChange={(e) =>
                                                setChipInput((prev) => ({ ...prev, [field]: e.target.value }))
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ',') {
                                                    e.preventDefault();
                                                    addChip(field, chipInput[field]);
                                                }
                                            }}
                                            placeholder={
                                                field === 'deviceCustomerId' ? 'Add integer...' : 'Add value...'
                                            }
                                            style={{
                                                border: 'none',
                                                outline: 'none',
                                                flexGrow: 1,
                                                minWidth: 80,
                                                fontSize: 14
                                            }}
                                        />
                                    )}
                                </Box>
                                {field === 'locationName' && (
                                    <Autocomplete
                                        options={locationNames.filter((n) => !selectedLocationNames.includes(n))}
                                        value={null}
                                        inputValue={chipInput[field]}
                                        onChange={(_, selected) => {
                                            if (selected) addChip(field, selected);
                                        }}
                                        onInputChange={(_, newValue, reason) => {
                                            if (reason !== 'reset') {
                                                setChipInput((prev) => ({ ...prev, [field]: newValue }));
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                placeholder="Add location..."
                                                variant="standard"
                                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    'data-testid': 'location-name-input',
                                                    style: { fontSize: 14, padding: '6px 12px' }
                                                }}
                                                sx={{ mt: 0.5 }}
                                            />
                                        )}
                                        fullWidth
                                    />
                                )}
                            </Box>
                        ))}
                        {availableFields.length > 0 && (
                            <Select
                                displayEmpty
                                value=""
                                size="small"
                                onChange={(e) => addSelectorField(e.target.value as SelectorField)}
                                renderValue={() => '+ Add selector field'}
                                sx={{ fontSize: 13 }}
                            >
                                {availableFields.map((f) => (
                                    <MenuItem key={f} value={f}>
                                        {SELECTOR_LABELS[f]}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            Value
                        </Typography>
                        <ValueInput
                            value={override.value ?? defaultValue}
                            valueType={valueType}
                            onChange={(v) => onChange({ ...override, value: v })}
                        />
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
}

interface OverrideEditorProps {
    overrides: SettingOverride[];
    defaultValue: unknown;
    valueType?: ValueType;
    onChange: (overrides: SettingOverride[]) => void;
}

function OverrideEditor({ overrides, defaultValue, valueType, onChange }: Readonly<OverrideEditorProps>): ReactElement {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const idCounterRef = useRef(0);
    const generateId = () => `override-stable-${idCounterRef.current++}`;
    const [stableIds, setStableIds] = useState<string[]>(() => overrides.map(() => generateId()));

    const { locations } = useGetLocations();
    const locationNames = locations?.map((l) => l.name) ?? [];

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return;
        const oldIdx = stableIds.indexOf(String(active.id));
        const newIdx = stableIds.indexOf(String(over.id));
        onChange(arrayMove(overrides, oldIdx, newIdx));
        setStableIds((prev) => arrayMove(prev, oldIdx, newIdx));
    };

    const handleDeleteOverride = (i: number) => {
        onChange(overrides.filter((_, idx) => idx !== i));
        setStableIds((prev) => prev.filter((_, idx) => idx !== i));
        if (expandedId === stableIds[i]) setExpandedId(null);
    };

    const addOverride = () => {
        const newId = generateId();
        onChange([...overrides, { selector: {}, value: defaultValue }]);
        setStableIds((prev) => [...prev, newId]);
        setExpandedId(newId);
    };

    return (
        <Box>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={stableIds} strategy={verticalListSortingStrategy}>
                    {overrides.map((override, i) => (
                        <SortableOverrideRow
                            key={stableIds[i]}
                            id={stableIds[i]}
                            override={override}
                            defaultValue={defaultValue}
                            valueType={valueType}
                            locationNames={locationNames}
                            isExpanded={expandedId === stableIds[i]}
                            onToggle={() => setExpandedId((prev) => (prev === stableIds[i] ? null : stableIds[i]))}
                            onChange={(updated) => {
                                const next = [...overrides];
                                next[i] = updated;
                                onChange(next);
                            }}
                            onDelete={() => handleDeleteOverride(i)}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            <Button startIcon={<AddIcon />} size="small" onClick={addOverride} data-testid="add-override-btn">
                Add override
            </Button>
        </Box>
    );
}

export default OverrideEditor;
